import React, { useState, useRef, useEffect } from "react";
import { ChevronDown, Menu, X } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { createPortal } from "react-dom";
import { useAnimationConfig, useReducedMotion } from "../lib/animations";

// Scroll Progress Bar Component
const ScrollProgressBar: React.FC = () => {
  const [scrollProgress, setScrollProgress] = useState(0);
  const config = useAnimationConfig();
  const isReducedMotion = useReducedMotion();

  useEffect(() => {
    const updateScrollProgress = () => {
      const scrollTop = window.scrollY;
      const docHeight =
        document.documentElement.scrollHeight - window.innerHeight;
      const progress = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      setScrollProgress(progress);
    };

    const throttledUpdate = throttle(updateScrollProgress, 16); // ~60fps
    window.addEventListener("scroll", throttledUpdate, { passive: true });
    updateScrollProgress(); // Initial call

    return () => window.removeEventListener("scroll", throttledUpdate);
  }, []);

  if (isReducedMotion) {
    return (
      <div className="h-1 bg-gray-200">
        <div
          className="h-full bg-[#FF5A1F] transition-all duration-300"
          style={{ width: `${scrollProgress}%` }}
        />
      </div>
    );
  }

  return (
    <div className="h-1 bg-gray-200 overflow-hidden">
      <motion.div
        className="h-full bg-gradient-to-r from-[#FF5A1F] to-[#e4501c] origin-left"
        initial={{ scaleX: 0 }}
        animate={{ scaleX: scrollProgress / 100 }}
        transition={{
          duration: config.customTimings.fast,
          ease: "easeOut",
        }}
      />
    </div>
  );
};

// Throttle utility function
const throttle = (func: Function, limit: number) => {
  let inThrottle: boolean;
  return function (this: any, ...args: any[]) {
    if (!inThrottle) {
      func.apply(this, args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
};

// Animated Dropdown Component
interface AnimatedDropdownProps {
  isOpen: boolean;
  onToggle: () => void;
  items: Array<{ name: string; href: string }>;
  isScrolled?: boolean;
  isHomePage?: boolean;
}

const AnimatedDropdown = React.forwardRef<
  HTMLDivElement,
  AnimatedDropdownProps & { isScrolled: boolean; isHomePage: boolean }
>(({ isOpen, onToggle, items, isScrolled, isHomePage }, ref) => {
  const config = useAnimationConfig();
  const isReducedMotion = useReducedMotion();

  const dropdownVariants = {
    closed: {
      opacity: 0,
      scale: 0.95,
      y: -10,
      transition: {
        duration: config.customTimings.fast,
        ease: config.customTimings.defaultEasing,
        when: "afterChildren",
        staggerChildren: 0.02,
        staggerDirection: -1,
      },
    },
    open: {
      opacity: 1,
      scale: 1,
      y: 0,
      transition: {
        duration: config.customTimings.normal,
        ease: config.customTimings.defaultEasing,
        when: "beforeChildren",
        staggerChildren: 0.05,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    closed: {
      opacity: 0,
      x: -10,
      transition: {
        duration: config.customTimings.fast,
      },
    },
    open: {
      opacity: 1,
      x: 0,
      transition: {
        duration: config.customTimings.normal,
        ease: config.customTimings.defaultEasing,
      },
    },
  };

  return (
    <div className="relative" ref={ref}>
      <motion.button
        onClick={onToggle}
        className={`flex items-center space-x-1 transition-colors duration-200 font-medium ${
          isScrolled
            ? "text-[#334155] hover:text-[#FF5A1F]"
            : isHomePage
            ? "text-white hover:text-[#FF5A1F]"
            : "text-black hover:text-[#FF5A1F]"
        }`}
        whileHover={isReducedMotion ? {} : { scale: 1.02 }}
        whileTap={isReducedMotion ? {} : { scale: 0.98 }}
      >
        <span>Home Plan</span>
        <motion.div
          animate={isReducedMotion ? {} : { rotate: isOpen ? 180 : 0 }}
          transition={{ duration: config.customTimings.fast }}
        >
          <ChevronDown className="w-4 h-4" />
        </motion.div>
      </motion.button>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            className="absolute top-full left-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-100 py-2 overflow-hidden"
            variants={isReducedMotion ? {} : dropdownVariants}
            initial="closed"
            animate="open"
            exit="closed"
          >
            {items.map((item, index) => (
              <motion.div
                key={item.name}
                variants={isReducedMotion ? {} : itemVariants}
                custom={index}
              >
                <Link
                  to={item.href}
                  className="block px-4 py-2 text-[#334155] hover:text-[#FF5A1F] hover:bg-[#F3F4F6] transition-colors duration-200"
                >
                  {item.name}
                </Link>
              </motion.div>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
});

AnimatedDropdown.displayName = "AnimatedDropdown";

// Magnetic Navigation Item Component
interface MagneticNavItemProps {
  href: string;
  isButton?: boolean;
  isActive?: boolean;
  isScrolled?: boolean;
  isHomePage?: boolean;
  children: React.ReactNode;
}

const MagneticNavItem: React.FC<MagneticNavItemProps> = ({
  href,
  isButton = false,
  isActive = false,
  isScrolled = false,
  isHomePage = false,
  children,
}) => {
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [isHovered, setIsHovered] = useState(false);
  const elementRef = useRef<HTMLAnchorElement>(null);
  const config = useAnimationConfig();
  const isReducedMotion = useReducedMotion();

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!elementRef.current || isReducedMotion) return;

    const rect = elementRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    const deltaX = e.clientX - centerX;
    const deltaY = e.clientY - centerY;

    // Limit magnetic effect distance
    const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
    const factor = Math.min(distance / 100, 1);

    setMousePosition({
      x: deltaX * factor * 0.3,
      y: deltaY * factor * 0.3,
    });
  };

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setMousePosition({ x: 0, y: 0 });
  };

  const baseClasses = isButton
    ? "bg-[#FF5A1F] text-white px-4 py-2 rounded-lg hover:bg-[#e4501c] transition-all duration-200 font-medium relative overflow-hidden"
    : `transition-colors duration-200 font-medium relative ${
        isActive
          ? "text-[#FF5A1F]"
          : isScrolled
          ? "text-[#334155] hover:text-[#FF5A1F]"
          : isHomePage
          ? "text-white hover:text-[#FF5A1F]"
          : "text-black hover:text-[#FF5A1F]"
      }`;

  if (isReducedMotion) {
    return (
      <Link to={href} className={baseClasses}>
        {children}
        {isActive && !isButton && (
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#FF5A1F]" />
        )}
      </Link>
    );
  }

  return (
    <motion.div className="relative">
      <motion.div
        animate={{
          x: mousePosition.x,
          y: mousePosition.y,
          scale: isHovered ? 1.05 : 1,
        }}
        transition={{
          type: "spring",
          stiffness: 300,
          damping: 30,
          mass: 0.8,
        }}
      >
        <Link
          ref={elementRef}
          to={href}
          className={baseClasses}
          onMouseMove={handleMouseMove}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          {children}

          {/* Active indicator for non-button items */}
          {isActive && !isButton && (
            <motion.div
              className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#FF5A1F] origin-left"
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{
                duration: config.customTimings.normal,
                ease: config.customTimings.defaultEasing,
              }}
            />
          )}

          {/* Hover effect for buttons */}
          {isButton && (
            <motion.div
              className="absolute inset-0 bg-white/10 rounded-lg"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{
                opacity: isHovered ? 1 : 0,
                scale: isHovered ? 1 : 0.8,
              }}
              transition={{
                duration: config.customTimings.fast,
              }}
            />
          )}
        </Link>
      </motion.div>
    </motion.div>
  );
};

// Animated Mobile Menu Button Component
interface MobileMenuButtonProps {
  isOpen: boolean;
  onClick: () => void;
  isScrolled?: boolean;
  isHomePage?: boolean;
}

const MobileMenuButton: React.FC<MobileMenuButtonProps> = ({
  isOpen,
  onClick,
  isScrolled = false,
  isHomePage = false,
}) => {
  const config = useAnimationConfig();
  const isReducedMotion = useReducedMotion();

  return (
    <button
      onClick={onClick}
      className={`md:hidden p-2 transition-colors duration-200 relative w-10 h-10 flex items-center justify-center ${
        isScrolled
          ? "text-[#334155] hover:text-[#FF5A1F]"
          : isHomePage
          ? "text-white hover:text-[#FF5A1F]"
          : "text-black hover:text-[#FF5A1F]"
      }`}
      aria-label={isOpen ? "Close menu" : "Open menu"}
    >
      <AnimatePresence mode="wait">
        {isOpen ? (
          <motion.div
            key="close"
            initial={{ opacity: 0, rotate: -90 }}
            animate={{ opacity: 1, rotate: 0 }}
            exit={{ opacity: 0, rotate: 90 }}
            transition={{ duration: 0.2 }}
          >
            <X className="w-6 h-6" />
          </motion.div>
        ) : (
          <motion.div
            key="menu"
            initial={{ opacity: 0, rotate: 90 }}
            animate={{ opacity: 1, rotate: 0 }}
            exit={{ opacity: 0, rotate: -90 }}
            transition={{ duration: 0.2 }}
          >
            <Menu className="w-6 h-6" />
          </motion.div>
        )}
      </AnimatePresence>
    </button>
  );
};

// Animated Mobile Menu Component
interface AnimatedMobileMenuProps {
  isOpen: boolean;
  homePlanItems: Array<{ name: string; href: string }>;
  navItems: Array<{ name: string; href: string; isButton?: boolean }>;
  onClose: () => void;
}

const AnimatedMobileMenu: React.FC<AnimatedMobileMenuProps> = ({
  isOpen,
  homePlanItems,
  navItems,
  onClose,
}) => {
  const config = useAnimationConfig();
  const isReducedMotion = useReducedMotion();

  const menuVariants = {
    closed: {
      opacity: 0,
      y: -10,
      transition: {
        duration: config.customTimings.fast,
        ease: config.customTimings.defaultEasing,
        when: "afterChildren",
        staggerChildren: 0.05,
        staggerDirection: -1,
      },
    },
    open: {
      opacity: 1,
      y: 0,
      transition: {
        duration: config.customTimings.normal,
        ease: config.customTimings.defaultEasing,
        when: "beforeChildren",
        staggerChildren: 0.1,
        delayChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    closed: {
      opacity: 0,
      x: -20,
      transition: {
        duration: config.customTimings.fast,
      },
    },
    open: {
      opacity: 1,
      x: 0,
      transition: {
        duration: config.customTimings.normal,
        ease: config.customTimings.defaultEasing,
      },
    },
  };

  const overlayVariants = {
    closed: {
      opacity: 0,
      backdropFilter: "blur(0px)",
      transition: {
        duration: config.customTimings.fast,
      },
    },
    open: {
      opacity: 1,
      backdropFilter: "blur(8px)",
      transition: {
        duration: config.customTimings.normal,
      },
    },
  };

  if (isReducedMotion) {
    return isOpen
      ? createPortal(
          <div className="md:hidden">
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black/20 z-[1000]"
              onClick={onClose}
            />
            {/* Top bar with logo and close */}
            <div
              className="fixed top-0 left-0 right-0 h-16 bg-white z-[1002] border-b border-gray-100"
              style={{ paddingTop: "env(safe-area-inset-top)" }}
            >
              <div className="max-w-[1280px] mx-auto px-6 md:px-8 h-full flex items-center justify-between">
                <Link to="/" className="flex items-center space-x-3">
                  <img
                    src="/images/logo3.png"
                    alt="Rolleston Homes"
                    className="h-10 w-auto"
                  />
                </Link>
                <button
                  onClick={onClose}
                  aria-label="Close menu"
                  className="p-2 text-[#334155] hover:text-[#FF5A1F]"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>
            {/* Menu */}
            <div
              className="fixed left-0 right-0 top-16 bottom-0 bg-white z-[1001] overflow-y-auto border-t border-gray-100"
              style={{ top: "calc(4rem + env(safe-area-inset-top))" }}
            >
              <div className="py-4 space-y-4">
                {/* Mobile Home Plan Section */}
                <div>
                  <div className="text-[#1F2937] font-semibold mb-2 px-6">
                    Home Plan
                  </div>
                  <div className="space-y-2 pl-10 pr-6">
                    {homePlanItems.map((item) => (
                      <Link
                        key={item.name}
                        to={item.href}
                        className="block text-[#334155] hover:text-[#FF5A1F] transition-colors duration-200 py-1"
                      >
                        {item.name}
                      </Link>
                    ))}
                  </div>
                </div>

                {/* Mobile Other Nav Items */}
                {navItems.map((item) => (
                  <Link
                    key={item.name}
                    to={item.href}
                    className={
                      item.isButton
                        ? "block mx-6 bg-[#FF5A1F] text-white px-4 py-3 rounded-lg hover:bg-[#e4501c] transition-colors duration-200 font-medium text-center shadow-lg"
                        : "block px-6 text-[#334155] hover:text-[#FF5A1F] transition-colors duration-200 font-medium py-2"
                    }
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            </div>
          </div>,
          document.body
        )
      : null;
  }

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop with blur effect */}
          <motion.div
            className="fixed inset-0 bg-black/20 z-[1000] md:hidden cursor-pointer"
            variants={overlayVariants}
            initial="closed"
            animate="open"
            exit="closed"
            onClick={onClose}
          />

          {/* Top bar with logo and close */}
          <div
            className="fixed top-0 left-0 right-0 h-16 bg-white z-[1002] border-b border-gray-100 md:hidden"
            style={{ paddingTop: "env(safe-area-inset-top)" }}
          >
            <div className="max-w-[1280px] mx-auto px-6 md:px-8 h-full flex items-center justify-between">
              <Link to="/" className="flex items-center space-x-3">
                <img
                  src="/images/logo3.png"
                  alt="Rolleston Homes"
                  className="h-10 w-auto"
                />
              </Link>
              <button
                onClick={onClose}
                aria-label="Close menu"
                className="p-2 text-[#334155] hover:text-[#FF5A1F]"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
          </div>

          {/* Animated Menu */}
          <motion.div
            className="fixed left-0 right-0 top-16 bottom-0 md:hidden bg-white z-[1001] overflow-y-auto border-t border-gray-100"
            style={{ top: "calc(4rem + env(safe-area-inset-top))" }}
            variants={menuVariants}
            initial="closed"
            animate="open"
            exit="closed"
          >
            <motion.div className="py-4 space-y-4">
              {/* Mobile Home Plan Section */}
              <motion.div variants={itemVariants}>
                <div className="text-[#1F2937] font-semibold mb-2 px-6">
                  Home Plan
                </div>
                <div className="space-y-2 pl-10 pr-6">
                  {homePlanItems.map((item, index) => (
                    <motion.div
                      key={item.name}
                      variants={itemVariants}
                      custom={index}
                    >
                      <Link
                        to={item.href}
                        className="block text-[#334155] hover:text-[#FF5A1F] transition-colors duration-200 py-1"
                      >
                        {item.name}
                      </Link>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* Mobile Other Nav Items */}
              {navItems.map((item, index) => (
                <motion.div
                  key={item.name}
                  variants={itemVariants}
                  custom={index + homePlanItems.length}
                  className="px-6"
                >
                  <Link
                    to={item.href}
                    className={
                      item.isButton
                        ? "block bg-[#FF5A1F] text-white px-4 py-3 rounded-lg hover:bg-[#e4501c] transition-all duration-200 font-medium text-center shadow-lg hover:shadow-xl transform hover:scale-105"
                        : "block text-[#334155] hover:text-[#FF5A1F] transition-colors duration-200 font-medium py-2"
                    }
                  >
                    {item.name}
                  </Link>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

const Header: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const scrollYRef = useRef(0);
  const location = useLocation();

  // Check if we're on the home page
  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };

    // Set initial state
    handleScroll();

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    setIsMenuOpen(false);
    setIsDropdownOpen(false);
  }, [location]);

  // Lock body scroll when mobile menu is open (iOS-safe)
  useEffect(() => {
    if (isMenuOpen) {
      scrollYRef.current = window.scrollY;
      const y = scrollYRef.current;
      // Prevent background scroll and layout shift
      document.body.style.position = "fixed";
      document.body.style.top = `-${y}px`;
      document.body.style.left = "0";
      document.body.style.right = "0";
      document.body.style.width = "100%";
      document.body.style.overflow = "hidden";
    } else {
      const styleTop = parseInt(document.body.style.top || "0", 10);
      const yFromStyle = Number.isFinite(styleTop) ? Math.abs(styleTop) : 0;
      const yStored = Number.isFinite(scrollYRef.current)
        ? scrollYRef.current
        : 0;
      const y = yStored || yFromStyle || 0;
      // Restore scroll and body styles
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
      if (y) {
        // Use rAF to ensure styles are applied before scrolling back
        window.requestAnimationFrame(() => {
          window.scrollTo(0, y);
        });
      }
    }

    return () => {
      // Cleanup in case component unmounts while menu open
      document.body.style.position = "";
      document.body.style.top = "";
      document.body.style.left = "";
      document.body.style.right = "";
      document.body.style.width = "";
      document.body.style.overflow = "";
    };
  }, [isMenuOpen]);

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const navItems = [
    { name: "Our Services", href: "/services" },
    { name: "About Us", href: "/about" },
    { name: "Get In Touch", href: "/contact", isButton: true },
  ];

  const homePlanItems = [
    { name: "View All Homes", href: "/homes" },
    { name: "Core Homes", href: "/homes/core" },
    { name: "Family Homes", href: "/homes/family" },
    { name: "Kiwiana Homes", href: "/homes/kiwiana" },
  ];

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "backdrop-blur-md shadow-sm"
          : "bg-transparent backdrop-blur-md"
      }`}
    >
      <ScrollProgressBar />
      <div className="max-w-[1280px] mx-auto px-6 md:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-3">
            <img
              src="/images/logo3.png"
              alt="Rolleston Homes"
              className="h-10 w-auto"
            />
            {/* <span className={`text-xl font-bold transition-colors duration-300 ${isScrolled ? 'text-[#1F2937]' : 'text-white'
              }`}>
              Rolleston Homes
            </span> */}
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center space-x-8">
            {/* Home Plan Dropdown */}
            <AnimatedDropdown
              isOpen={isDropdownOpen}
              onToggle={toggleDropdown}
              items={homePlanItems}
              isScrolled={isScrolled}
              isHomePage={isHomePage}
              ref={dropdownRef}
            />

            {/* Other Nav Items */}
            {navItems.map((item) => (
              <MagneticNavItem
                key={item.name}
                href={item.href}
                isButton={item.isButton}
                isActive={location.pathname === item.href}
                isScrolled={isScrolled}
                isHomePage={isHomePage}
              >
                {item.name}
              </MagneticNavItem>
            ))}
          </nav>

          {/* Mobile Menu Button */}
          <MobileMenuButton
            isOpen={isMenuOpen}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            isScrolled={isScrolled}
            isHomePage={isHomePage}
          />
        </div>

        {/* Mobile Menu */}
        <AnimatedMobileMenu
          isOpen={isMenuOpen}
          homePlanItems={homePlanItems}
          navItems={navItems}
          onClose={() => setIsMenuOpen(false)}
        />
      </div>
    </header>
  );
};

export default Header;
